import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {animate, PanInfo, useMotionValue} from 'framer-motion';

import useWindowSize from '@/hooks/WindowSize/WindowSize';

import {CarouselItemProps} from '.';
import {
  ANIMATE_TRANSLATE_ITEMS,
  ANIMATE_X_TRANSLATE_LONG,
  ANIMATE_X_TRANSLATE_SHORT,
  FULL_PERCENTAGE,
  SWIPE_POWER,
} from './models/constants';

export interface CarouselProps {
  items: CarouselItemProps[];
  itemsPerSlide: number;
  alwaysShowArrows?: boolean;
  alwaysShowDots?: boolean;
  deactivateDots?: boolean;
  extraItemPercentage?: number;
  buttonText?: string;
  onClickItem?: (id: string | number) => void;
  setCurrentCustomSlide?: Dispatch<SetStateAction<number>>;
}

export const useActions = ({
  items,
  itemsPerSlide,
  extraItemPercentage,
  deactivateDots,
  onClickItem,
  setCurrentCustomSlide,
}: CarouselProps) => {
  //#region [---- [PROPERTIES] ----]
  const totalSlides = Math.ceil(items.length / itemsPerSlide);
  const {isMobile} = useWindowSize();
  const x = useMotionValue(0);
  //#endregion

  //#region [---- [HOOKS] ----]
  const [currentSlide, setCurrentSlide] = useState(0);

  const getTranslateX = useMemo(() => {
    const itemWidth = FULL_PERCENTAGE / (itemsPerSlide + extraItemPercentage);
    const offset = currentSlide * itemsPerSlide * itemWidth;
    return -offset;
  }, [currentSlide, extraItemPercentage, itemsPerSlide]);

  const getDragConstraints: any = useMemo(() => {
    return currentSlide === 0 ? {left: 'auto', right: 0} : false;
  }, [currentSlide]);

  const shouldFitToSlide = useMemo(
    () => items.length > itemsPerSlide,
    [items.length, itemsPerSlide],
  );

  const shouldEnableSwipe = useMemo(
    () => isMobile && shouldFitToSlide,
    [isMobile, shouldFitToSlide],
  );

  const animatedTranslation = useMemo(
    () =>
      -(
        (totalSlides > ANIMATE_TRANSLATE_ITEMS
          ? ANIMATE_X_TRANSLATE_LONG
          : ANIMATE_X_TRANSLATE_SHORT) * totalSlides
      ),
    [totalSlides],
  );

  useEffect(() => {
    const trackEventToRun = items[currentSlide].trackEvent;
    if (trackEventToRun) {
      trackEventToRun();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide]);
  //#endregion

  //#region [---- [LOGIC] ----]
  const paginate = useCallback(
    (direction: number) => {
      const nextSlide = currentSlide + direction;
      if (nextSlide >= 0 && nextSlide < totalSlides) {
        setCurrentSlide(nextSlide);
        setCurrentCustomSlide(nextSlide);
      }
    },
    [currentSlide, setCurrentCustomSlide, totalSlides],
  );

  const onClickDot = useCallback(
    (index: number) => {
      if (!deactivateDots) {
        setCurrentSlide(index);
        setCurrentCustomSlide(index);
      }
    },
    [deactivateDots, setCurrentCustomSlide],
  );

  const onClickNextSlide = useCallback(() => {
    paginate(1);
  }, [paginate]);

  const onClickButton = useCallback(() => {
    onClickNextSlide();
    onClickItem(currentSlide);
  }, [currentSlide, onClickItem, onClickNextSlide]);

  const onClickPrevSlide = useCallback(() => {
    paginate(-1);
  }, [paginate]);

  const onDragEnds = useCallback(
    (info: PanInfo) => {
      const swipeVelocity = info.velocity.x;

      if (swipeVelocity < -SWIPE_POWER && currentSlide < totalSlides - 1) {
        onClickNextSlide();
      } else if (swipeVelocity > SWIPE_POWER && currentSlide > 0) {
        onClickPrevSlide();
      } else if (
        swipeVelocity < -SWIPE_POWER &&
        currentSlide === totalSlides - 1
      ) {
        animate(x, animatedTranslation, {
          type: 'spring',
          stiffness: 300,
          damping: 150,
          mass: 3,
        });
      }
    },
    [
      animatedTranslation,
      currentSlide,
      onClickNextSlide,
      onClickPrevSlide,
      totalSlides,
      x,
    ],
  );

  const onSelectCarouselItem = useCallback(
    (id: string) => {
      onClickItem(id);
    },
    [onClickItem],
  );
  //#endregion

  return {
    x,
    isMobile,
    currentSlide,
    totalSlides,
    getTranslateX,
    getDragConstraints,
    shouldFitToSlide,
    shouldEnableSwipe,
    onClickDot,
    onClickNextSlide,
    onClickPrevSlide,
    onDragEnds,
    onSelectCarouselItem,
    onClickButton,
  };
};
